import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import Sidebar from './Sidebar'
import ThemeToggle from './ThemeToggle'
import logo from '@/assets/vision_controller_logo.jpg'

export default function AppLayout() {
  const { user, logout, nav } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // React Router gives the very first history entry of a session the key
  // "default" (no prior entry to go back to) - a plain, well-established
  // heuristic for "is there anywhere to go back to" without tracking our
  // own navigation stack. Once the user has navigated anywhere (e.g. a
  // sidebar click), this flips true and stays true for the rest of the tab's
  // session, even after going back once.
  //
  // That heuristic alone still shows "Back" on the dashboard itself once
  // it's been reached via a client-side navigation rather than a fresh
  // load (confirmed live: a client role, which has no sidebar to click
  // through, still saw it on their own portfolio list after navigating
  // there and back) - the dashboard is the semantic top of navigation for
  // every role, never a place "Back" is meaningful, so it's excluded here
  // regardless of history state.
  const canGoBack = location.key !== 'default' && location.pathname !== '/'

  // Small screens (below md) have no room for the sidebar rail beside the
  // content, so the same Sidebar tree opens as an off-canvas drawer from a
  // menu button instead. Clients have no nav entries (Sidebar renders
  // nothing for them), so they get no menu button either.
  const hasNav = nav.length > 0
  const [navOpen, setNavOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const closeNav = useCallback(() => {
    setNavOpen(false)
    menuButtonRef.current?.focus()
  }, [])

  useEffect(() => setNavOpen(false), [location.pathname, location.search])

  useEffect(() => {
    if (!navOpen) return
    drawerRef.current?.querySelector<HTMLElement>('button, a[href]')?.focus()
    // Widening past md swaps the drawer for the rail; don't leave it open
    // behind the scenes to reappear on the next narrowing.
    const desktop = window.matchMedia('(min-width: 768px)')
    const onChange = () => desktop.matches && setNavOpen(false)
    desktop.addEventListener('change', onChange)
    return () => desktop.removeEventListener('change', onChange)
  }, [navOpen])

  function onDrawerKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      closeNav()
      return
    }
    if (e.key !== 'Tab' || !drawerRef.current) return
    // Keep focus inside the modal drawer.
    const focusable = Array.from(
      drawerRef.current.querySelectorAll<HTMLElement>('button, a[href]'),
    ).filter((el) => el.offsetParent !== null)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-shell text-text-primary">
      <header className="flex h-16 shrink-0 items-center justify-between gap-2 bg-shell pl-2 pr-2 sm:pl-6 sm:pr-5">
        <div className="flex min-w-0 items-center gap-1 sm:gap-3">
          {hasNav && (
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
              aria-expanded={navOpen}
              aria-controls="mobile-nav"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink hover:bg-nav-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand md:hidden"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                aria-hidden="true"
                className="h-5 w-5"
              >
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {/* Plain <a>, not react-router's <Link>: a genuine full browser
              reload back to the dashboard, matching the same "click the name
              to reload" behavior as the Details page's site name - not a
              client-side route transition. */}
          <a href="/" className="flex shrink-0 items-center gap-2.5 hover:opacity-90">
            {/* The logo JPG has a white background: multiply blends it into
                the light shell; dark mode sits it on a small white tile. */}
            <img
              src={logo}
              alt="Vision Portal"
              className="h-[34px] w-[34px] object-contain mix-blend-multiply dark:rounded-lg dark:bg-white dark:p-0.5 dark:mix-blend-normal"
            />
            <span className="hidden text-lg font-bold text-brand sm:inline">Vision Portal</span>
          </a>
          {canGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              title="Go back"
              aria-label="Go back"
              className="flex h-10 shrink-0 items-center gap-1 rounded-full px-3 text-base font-normal text-ink hover:bg-nav-hover sm:px-4"
            >
              <span aria-hidden="true">←</span> <span className="hidden sm:inline">Back</span>
            </button>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 text-sm sm:gap-4">
          <ThemeToggle />
          <Link
            to="/profile"
            className="flex items-center gap-2.5 rounded-full p-1 text-nav-text hover:bg-nav-hover hover:text-text-primary hover:no-underline md:pr-3.5"
          >
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-nav-active text-sm font-bold uppercase text-nav-active-text"
            >
              {(user?.firstname || user?.email || '?').charAt(0)}
            </span>
            {/* sr-only below md: the avatar alone stays tappable and the
                link keeps the same accessible name at every width. */}
            <span className="sr-only md:not-sr-only">{user?.email}</span>
          </Link>
          <button
            type="button"
            onClick={() => logout()}
            className="flex h-10 items-center gap-2 rounded-full px-3 text-base font-normal text-ink hover:bg-nav-hover sm:px-4"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="h-[18px] w-[18px]"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
            <span className="sr-only sm:not-sr-only">Log out</span>
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        {/* The content sits on a rounded surface floating on the shell
            colour; <main> itself is that surface (rather than a wrapper
            inside it) so pages that size themselves with h-full, like the
            Dashboard, still resolve against a definite height. */}
        <main className="mx-2 mb-2 min-w-0 flex-1 overflow-y-auto rounded-2xl bg-card p-4 sm:p-5 md:mb-4 md:ml-1 md:mr-4 md:rounded-3xl md:p-7">
          <Outlet />
        </main>
      </div>
      {hasNav && navOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={closeNav} />
          <div
            ref={drawerRef}
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            onKeyDown={onDrawerKeyDown}
            // A link that doesn't change the URL (the current page) still
            // dismisses the menu; route changes are handled by the effect.
            onClick={(e) => (e.target as Element).closest('a') && setNavOpen(false)}
            className="absolute inset-y-0 left-0 flex w-[min(300px,85vw)] flex-col bg-shell shadow-[0_10px_28px_rgba(16,40,26,0.16),0_1px_3px_rgba(16,40,26,0.08)]"
          >
            <div className="flex h-16 shrink-0 items-center justify-between pl-5 pr-2">
              <span className="text-lg font-bold text-brand">Vision Portal</span>
              <button
                type="button"
                onClick={closeNav}
                aria-label="Close navigation"
                className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-nav-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  aria-hidden="true"
                  className="h-5 w-5"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <Sidebar variant="drawer" />
          </div>
        </div>
      )}
    </div>
  )
}
